import { phone, cep, cpf } from "src/framework/commons/masks";
import { DEFAULT_PROJETO, GARANTIA_TEMPO_UNIDADE, INVERSOR_TENSAO_ATTR_ID, INVERSOR_TIPO_CONEXAO_ATTR_ID, POSICAO_MODULO, PROPOSTA_OPTIONS, TIPO_FATURAMENTO, TIPOS_INVERSOR_OPTIONS } from "../constants";
import { Consumo, Fileira,  OptionWithImage,  Projeto as ProjetoStore, Sugestao } from "../types";
import { ComponenteProjetoComQuantidade, ProjetoOutput } from "./types";
import { Atributo } from "src/providers/cadastros/AtributosProvider";
import { Conta, Estado, Regiao } from "src/types";
import { cnpj } from "src/framework/commons/masks";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { Componente, ID_INVERSOR, ID_MICROINVERSOR, ID_MODULO } from "src/components/componentes/types";
import { safeGeocodeByAddress } from "src/framework/commons/MapsUtil";

interface Props {
  estados: Estado[]
  componentesDoProjetoComQuantidade: ComponenteProjetoComQuantidade[]
  cachedCategoriasEstruturasIds: number[]
  cachedCategoriasAcessoriosIds: number[]
  cachedGrupoEstrutura: any
  cachedAtributos: Atributo[]
  cachedComponentes: Componente[];
  regioes: any[];
  estadosServico: any[];
}

export class ProjetoHelper {
  constructor(private data: ProjetoOutput, private readonly props: Props){
    this.data = this.normalizar()
  }

  getLatLngFromAddress = async (): Promise<{ lat: number; lng: number }> => {
    const { logradouro, numero, bairro, cep }  = this.data.endereco;
    const { cidade, estado_id } = this.data
    const endereco = `${logradouro || ''}, ${numero || ''}, ${bairro || ''}, ${cidade?.nome || ''}, ${estado_id}, ${cep}`
    try {
      const res = await safeGeocodeByAddress(endereco)
      if (!res?.[0]) return { lat: 0, lng: 0 }
      return getLatLng(res[0])
    } catch (error) {
      return {
        lat: 0,
        lng: 0,
      }
    }
  }

  normalizar = (): ProjetoOutput => {
    const maskedPhone = phone({ target: { value: this.data.telefone_proposta || '' } })?.target?.value || ''
    const maskedCep = cep({ target: { value: this.data.endereco?.cep || '' } })?.target?.value || ''
    const maskedCpfCnpj = this.formatarCpfOuCnpj(this.data.cpf_cnpj_proposta)

    return {
      ...this.data,
      telefone_proposta: maskedPhone,
      cpf_cnpj_proposta: maskedCpfCnpj,
      endereco: {
        ...(this.data.endereco || {}),
        cep: maskedCep,
      },
    };
  }

  formatarCpfOuCnpj = (value: string) => {
    if (!value) return '';
    const formatValue = value?.replace(/[^a-zA-Z0-9]/g, '')
    return formatValue.length === 14 ? cnpj({ target: { value: formatValue } })?.target?.value || '' : cpf({ target: { value: formatValue } })?.target?.value || ''
  }

  buscarAtributo = (id: string) => {
    return this.props.cachedAtributos.find(item => item.id === id)
  }

  formatItem = (item: ComponenteProjetoComQuantidade) => {
    return {
      altura: Number(item.altura || 0),
      categoria: '',
      estruturas_grupo_id: item.estruturas_grupo_id,
      categoria_componentes_id: Number(item.categoria_componentes_id || 0),
      componente_id: Number(item.id || 0),
      comprimento: Number(item.comprimento || 0),
      descricao: item.descricao || '',
      inversoresTipos: [],
      largura: Number(item.largura || 0),
      nome: item.nome || '',
      modelo: item.modelo || '',
      peso: Number(item.peso || 0),
      potencia: 0,
      quantidade: Number(item.quantidade || 0),
      selecionado: true,
      valor: 0,
      valor_unitario: item.valor_unitario || 0,
    }
  }

  buscarItensPorCategoriaId = (ids: number | number[]) => {
    if (Array.isArray(ids)) {
      return this.props.componentesDoProjetoComQuantidade.filter(item => {
        return ids.includes(Number(item.categoria_componentes_id)) && item.considerar_calculo
      }).map(this.formatItem) || []
    }
    return this.props.componentesDoProjetoComQuantidade.filter(item => item.categoria_componentes_id === ids && item.considerar_calculo).map(this.formatItem) || []
  }

  buscarEstado = (): { id: string; nome: string } | undefined => {
    if (!this.data.estado_id) return undefined;
    const estado = this.props.estados?.find((item) => item.id === this.data.estado_id)
    return estado ? { id: estado.id, nome: estado.nome } : undefined
  }

  getTipoInversorId = (inversores: any) => {
    if (this.data.tipo === 'personalizado') return inversores.length > 0 ? ID_INVERSOR : ID_MICROINVERSOR
    if (this.data.tipo === 'dimensionado') return this.data.solucao_sugerida?.tipo_inversor
    return 0
  }

  formatarConsumo = () => {
    const consumo: Consumo[] = []
    if (this.data?.solucao_sugerida?.consumo){
      this.data.solucao_sugerida.consumo.forEach((item: any) => {
        consumo.push({
          day: item.dia,
          value: item.consumo
        })
      })
    }

    return consumo;
  }
  
  formatarSugestoes = (): Sugestao => {
    if (!this.data.solucao_sugerida) return DEFAULT_PROJETO.sugestoes;

    const componentesSugeridosProjeto = this.data.solucao_sugerida.componentes || []

    const modulos = this.props.cachedComponentes.filter((item: any) => item.categoria_componentes_id === ID_MODULO)

    const inversores = this.props.cachedComponentes.filter((item: any) => item.categoria_componentes_id === ID_INVERSOR)

    const acessorios = this.props.cachedComponentes.filter((item: any) => {
      return this.props.cachedCategoriasAcessoriosIds.includes(item.categoria_componentes_id)
    })
    const estruturas = this.props.cachedComponentes.filter((item: any) => {
      return this.props.cachedCategoriasEstruturasIds.includes(item.categoria_componentes_id)
    })

    const moduloSugerido = componentesSugeridosProjeto.find(item => {
      return modulos.map((item: any) => item.id).includes(item.id)
    })

    const inversoresSugerido = componentesSugeridosProjeto.filter(item => {
      return inversores.map((item: any) => item.id).includes(item.id)
    })?.map(item => ({ id: item.id, quantidade: item.quantidade, categoria_componentes_id: item.categoria_componentes_id }))

    const acessoriosSugerido = componentesSugeridosProjeto.filter(item => {
      return acessorios.map((item: any) => item.id).includes(item.id)
    })?.map(item => ({ id: item.id, quantidade: item.quantidade, categoria_componentes_id: item.categoria_componentes_id }))

    const estruturasSugerido = componentesSugeridosProjeto.filter(item => {
      return estruturas.map((item: any) => item.id).includes(item.id)
    })?.map(item => ({ id: item.id, quantidade: item.quantidade, categoria_componentes_id: item.categoria_componentes_id }))

    return {
      modulo: moduloSugerido ? { categoria_componentes_id: moduloSugerido.categoria_componentes_id, id: moduloSugerido.id, quantidade: moduloSugerido.quantidade } : DEFAULT_PROJETO.sugestoes.modulo,
      acessorios: acessoriosSugerido || DEFAULT_PROJETO.sugestoes.acessorios,
      inversores: inversoresSugerido || DEFAULT_PROJETO.sugestoes.inversores,
      estrutura: estruturasSugerido || DEFAULT_PROJETO.sugestoes.estrutura,
    }
  }

  mostrarOpcaoServico = () => {
    if (!this.data?.id) return true;
    const reg = this.props.regioes || []
    const estadosServico = this.props.estadosServico || []
    const estadosPermitemServico: string[] = estadosServico.map((item: any) => item.regiao_id)
    const currentRegioes: string[] = reg.filter((item: Regiao) => {
      if (!item.estados || item.estados.length === 0) return false;
      const ids = item.estados.map((estado: any) => estado.estado_id)
      return ids.includes(this.data.estado_id)
    }).map((item: any) => item.id)
    return estadosPermitemServico.some(item => {
      if (currentRegioes.includes(item)) return true;
      return false;
    })
  }
  
  montarInversorModelo = (): OptionWithImage<string> | null => {
    if (!this.data?.solucao_sugerida?.inversor_modelo) return null;
    return {
      text: this.data?.solucao_sugerida?.inversor_modelo?.toLocaleUpperCase(),
      value: this.data?.solucao_sugerida?.inversor_modelo,
      image: "",
      disabled: false,
    }
  }

  getRegioes = () => {
    if (!this.data.estado_id) return []
    return this.props.regioes?.filter((regiao: any) => {
      const estadosList = regiao.estados || []


      const estados = estadosList.map((item: any) => item.estado_id)
      return estados.includes(this.data.estado_id)
    })
  }

  formatar = async () => {
    const { cachedCategoriasAcessoriosIds, cachedCategoriasEstruturasIds, cachedGrupoEstrutura } = this.props;

    const tensao = this.buscarAtributo(INVERSOR_TENSAO_ATTR_ID)
    const numFases = this.buscarAtributo(INVERSOR_TIPO_CONEXAO_ATTR_ID)

    const acessorios = this.buscarItensPorCategoriaId(cachedCategoriasAcessoriosIds)
    const estruturas = this.buscarItensPorCategoriaId(cachedCategoriasEstruturasIds)
    const modulos = this.buscarItensPorCategoriaId(ID_MODULO)
    const inversores = this.buscarItensPorCategoriaId(ID_INVERSOR)
    const microInversores = this.buscarItensPorCategoriaId(ID_MICROINVERSOR)

    const tipoInversorId = this.getTipoInversorId(inversores)
    const tipoInversor = TIPOS_INVERSOR_OPTIONS.find(item => item.value === tipoInversorId) || null
    const tipoProposta = PROPOSTA_OPTIONS.find(item => item.value === (!!this.data.valor_servico ? 'gerador-com-instalacao' : 'gerador-sem-instalacao')) || null
    const tempoGarantiaUnidade = GARANTIA_TEMPO_UNIDADE.find(item => item.value === this.data.tempo_garantia_servico_unidade) || null

    const grupos = cachedGrupoEstrutura?.filter((item: any) => {
      const ids = this.data?.disposicoes?.map((item) => item.grupo_estrutura_id)
      return ids?.includes(item.id)
    })

    const componentesProjeto: any[] = this.data?.componentes || [];

    const latLngFromAddress = await this.getLatLngFromAddress()

    const payload: ProjetoStore = {
      ...DEFAULT_PROJETO,
      regioes: this.getRegioes(),
      latitude: latLngFromAddress.lat,
      longitude: latLngFromAddress.lng,
      analista: (this.data.analista as unknown) as Conta,
      analista_id: this.data.analista_id,
      analista_nome: this.data.analista?.nome,
      anexos: this.data.anexos,
      assistente_id: this.data.assistente_id,
      cidade: this.data.cidade ? { estado_id: this.data.cidade.estado_id, cidade_codigo: this.data.cidade.cidade_codigo, id: this.data.cidade.id, nome: this.data.cidade.nome } : null,
      cidade_id: this.data.cidade?.id,
      estado_id: this.data.estado_id,
      auto_comissao: this.data.auto_comissao,
      cidade_nome: this.data.cidade.nome,
      cliente_proposta: this.data.cliente_proposta,
      codigo_venda: this.data.codigo_venda,
      cpf_cnpj_proposta: this.data.cpf_cnpj_proposta,
      created_at: this.data.created_at,
      data_etapa_venda: this.data.data_etapa_venda,
      distribuidor: (this.data.distribuidor as unknown) as Conta,
      distribuidor_id: this.data.distribuidor?.id,
      email_proposta: this.data.email_proposta,
      etapa_venda: this.data.etapa_venda,
      ev: (this.data.ev as unknown) as Conta,
      ev_id: this.data.ev?.id,
      ev_nome: this.data?.ev?.nome,
      fantasia_distribuidor: this.data.distribuidor.fantasia,
      fantasia_revenda: this.data.revenda?.fantasia,
      id: this.data.id,
      observacao: this.data.observacao,
      valor_servico_pis_cofins: this.data.valor_servico_pis_cofins,
      valor_servico: this.data.valor_servico,
      codigo_servico: this.data.codigo_servico,
      percentual_comissao_revenda: this.data.percentual_comissao_revenda,
      potencia: this.data.potencia,
      potencia_inversor: this.data.potencia_inversor,
      segmento: this.data.segmento,
      valor_margem_produtos: 0,
      percentual_comissao_distribuidor: this.data.percentual_comissao_distribuidor,
      valor_anual_manutencao: this.data.valor_anual_manutencao,
      valor: this.data.valor,
      uploads: this.data.uploads,
      titulo: this.data.titulo,
      tipo_faturamento: this.data.tipo_faturamento || TIPO_FATURAMENTO[0].value || null,
      tipo: this.data.tipo,
      tempo_garantia_servico: this.data.tempo_garantia_servico,
      supervisor_id: this.data.supervisor_id,
      solicitacao: this.data.solicitacao,
      situacao: this.data.situacao,
      servico: this.data.servico,
      revenda_id: this.data.revenda?.id,
      revenda: this.data.revenda ? (this.data.revenda as unknown) as Conta : null,
      prioridade: this.data.prioridade,
      isEdit: true,
      estado: this.buscarEstado(),
      estado_original: this.buscarEstado(),
      modulosList: modulos,
      cep: this.data.endereco.cep,
      acessorios,
      estruturas_edicao: estruturas,
      tipo_proposta: tipoProposta,
      preco_kwh_com_imposto: this.data.preco_kwh,
      preco_kwh_com_imposto_original: this.data.preco_kwh,
      numero: this.data.endereco?.numero || '',
      endereco_proposta: this.data.endereco?.logradouro || '',
      bairro: this.data.endereco?.bairro || '',
      disposicoes: grupos,
      telefone_proposta: this.data.telefone_proposta,
      tipo_inversor: tipoInversor,
      inversoresList: [...inversores, ...microInversores],
      termo_aceito: this.data?.valor_projeto > 0,
      tempo_garantia_servico_unidade: tempoGarantiaUnidade,
      cep_encontrado: !!this.data.endereco.cep,
      tensao_rede: this.data?.solucao_sugerida?.inversor_tensao ? {
        text: tensao?.valores?.enum.find((item) => item.value === this.data?.solucao_sugerida?.inversor_tensao)?.text || '',
        value: tensao?.valores?.enum.find((item) => item.value === this.data?.solucao_sugerida?.inversor_tensao)?.value || 0
      } : null,
      numero_fases: this.data?.solucao_sugerida?.inversor_tipo_conexao ? {
        text: numFases?.valores?.enum.find(item => item.value === this.data?.solucao_sugerida?.inversor_tipo_conexao)?.text || '',
        value: numFases?.valores?.enum.find(item => item.value === this.data?.solucao_sugerida?.inversor_tipo_conexao)?.value || 0
      } : null,
      fator_perdas: Number(this.data?.solucao_sugerida?.fator_perdas || 0),
      tipo_projeto: DEFAULT_PROJETO.tipo_projeto,
      potencia_desejada: Number(this.data?.solucao_sugerida?.potencia || 0),
      consumo_meses: this.formatarConsumo(),
      modo_calculo: this.formatarConsumo().length > 0 ? 'consumo': 'potencia',
      consumo_data_inicial: this.formatarConsumo().length > 0 ? new Date(this.formatarConsumo()[0].day) : null,
      consumo_data_final: this.formatarConsumo().length > 0 ? new Date(this.formatarConsumo()[this.formatarConsumo().length - 1].day) : null,
      fileiras: this.data?.solucao_sugerida?.fileiras?.length ? [] : DEFAULT_PROJETO.fileiras,
      fileiras_edicao: this.data?.solucao_sugerida?.fileiras?.length ? this.data?.solucao_sugerida?.fileiras.map((item: any): Fileira => ({
        fileiras: item.fileiras_iguais,
        posicao_modulo: POSICAO_MODULO.find(posicao=> posicao.value === item.posicao) || null,
        quantidade_modulos: item.modulos_sequencia,
        tipo_estrutura: { value: item.estrutura_id, image: '', text: '', estruturas_grupo_id: item?.estruturas_grupo_id },
      })) : [],
      sugestoes: this.formatarSugestoes(),
      simultaneidade: this.data.simultaneidade || 0,
      recebe_servico: this.mostrarOpcaoServico(),
      simulacao_financiamento: this.data.simulacao_financiamento ? {
        ...this.data.simulacao_financiamento,
        cpfCnpj: this.formatarCpfOuCnpj(this.data.simulacao_financiamento.cpfCnpj)
      } : null,
      lista_termo_simulacao_financiamento: this.data.lista_termo_simulacao_financiamento ? this.data.lista_termo_simulacao_financiamento : null,
      dados_termo: this.data.dados_termo || undefined,
      documentos_servico: this.data?.documentos_servico || undefined,
      componentes: componentesProjeto || undefined,
      componentes_finame: this.data.componentes_finame || undefined,
      componentes_modificados: this.data?.componentes_modificados || undefined,
      tarifa_concessionaria_id: this.data.tarifa_concessionaria_id || 0,
      tarifa_concessionaria_id_original: this.data.tarifa_concessionaria_id || 0,
      showRoiPayback: false,
      peso_gerador: this.data.peso_gerador || 0,
      peso_telhado: this.data.peso_telhado || 0,
      dados_concessionaria_payback: this.data.dados_concessionaria_payback,
      impostos_payback: this.data.impostos_payback,
      justificativa_valor_servico: this.data.justificativa_valor_servico || null,
      inversor_modelo:  this.montarInversorModelo()
    }

    return payload;
  }
}